import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { Quote } from '../types/api';
import { getQuotes } from '../services/quotes.service';
import { QuoteTable } from '../components/quote-table.component';
import { useStickyState } from '../util';
import { EditQuoteRoute, ViewQuoteRoute } from '../routes';
import Search from '../components/search.component';

export default function QuotesView() {
  const navigate = useNavigate();

  // Sticky state for persistence
  const [quoteData, setQuoteData] = useStickyState<Quote[]>([], 'quotes-data');
  const [searchTerm, setSearchTerm] = useStickyState<string>('', 'quotes-search');
  const [searchTermFinal, setSearchTermFinal] = useStickyState<string>('', 'quotes-search-final');
  const [currentSkip, setCurrentSkip] = useStickyState<number>(0, 'quotes-skip');
  const [currentTake, setCurrentTake] = useStickyState<number>(100, 'quotes-take');
  const [statusFilter, setStatusFilter] = useStickyState<string>('', 'quotes-status-filter');
  const [showArchived, setShowArchived] = useStickyState<boolean>(false, 'quotes-show-archived');
  const [totalRows, setTotalRows] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch quotes
  const fetchQuotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, totalRows: count } = await getQuotes({
        skip: currentSkip,
        take: currentTake,
        search: searchTermFinal || undefined,
        status: statusFilter || undefined,
        archived: showArchived ? true : false,
      });
      setQuoteData(data);
      setTotalRows(count);
    } catch (error: any) {
      console.error('Error fetching quotes:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch quotes. Is the backend running?';
      setError(errorMessage);
      setQuoteData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Debug: Log the current state
  useEffect(() => {
    console.log('Quotes Debug:', {
      dataLength: quoteData.length,
      totalRows,
      firstQuote: quoteData[0],
    });
  }, [quoteData, totalRows]);

  useEffect(() => {
    fetchQuotes();
  }, [currentSkip, currentTake, searchTermFinal, statusFilter, showArchived]);

  useEffect(() => {
    setCurrentSkip(0);
  }, [statusFilter, showArchived]);

  const handleRowClick = (quote: Quote) => {
    navigate(ViewQuoteRoute, { state: { quote } });
  };

  const handleCreateQuote = () => {
    navigate(EditQuoteRoute, { state: { mode: 'create' } });
  };

  const handleFilterChange = (status: string) => {
    setStatusFilter(status);
  };

  const handleSearch = (st: string) => {
    setSearchTerm(st);
    setSearchTermFinal(st);
    setCurrentSkip(0);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchTermFinal('');
    setCurrentSkip(0);
  };

  const filterButtons = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Accepted', value: 'ACCEPTED' },
    { label: 'Declined', value: 'DECLINED' },
  ];

  return (
    <>
      <Typography variant="h1">Quotes</Typography>
      <Box sx={{ m: 1, textAlign: 'right' }}>
        <Button
          variant="contained"
          size="small"
          color="inherit"
          onClick={handleCreateQuote}
        >
          Create Quote
        </Button>
      </Box>

      {/* Search bar */}
      <Box sx={{ m: 1 }}>
        <div style={{ textAlign: 'center' }}>
          <Search
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            doSearch={handleSearch}
            onReset={handleReset}
            placeholderText="Search by quote number, customer name, or email..."
            clearOnEnter={false}
          />
        </div>
      </Box>

      {/* Filter buttons */}
      <Box sx={{ m: 1, mt: 3, display: 'flex', gap: 0, flexWrap: 'wrap' }}>
        {filterButtons.map((button) => {
          const sx =
            button.value === statusFilter
              ? { borderBottom: '2px solid rgb(186, 34, 36)' }
              : {};
          return (
            <Typography key={button.value} sx={sx}>
              <Button
                size="small"
                variant="text"
                onClick={() => handleFilterChange(button.value)}
              >
                {button.label}
              </Button>
            </Typography>
          );
        })}

        <Typography sx={showArchived ? { borderBottom: '2px solid rgb(186, 34, 36)' } : {}}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
        </Typography>
      </Box>

      {/* Error message */}
      {error && (
        <Box sx={{ m: 1, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          <Typography variant="body1">
            <strong>Error:</strong> {error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Check the console and network tab for more details. Make sure the backend is running.
          </Typography>
        </Box>
      )}

      {/* Quotes table */}
      <Box sx={{ m: 1, mt: 0 }}>
        <QuoteTable
          data={quoteData}
          pageSize={currentTake}
          totalRows={totalRows}
          currentPage={Math.floor(currentSkip / currentTake)}
          onPageChange={(page: number) => setCurrentSkip(page * currentTake)}
          onPageSizeChange={(newPageSize: number) => {
            setCurrentTake(newPageSize);
            setCurrentSkip(0);
          }}
          onRowClick={handleRowClick}
          loading={loading}
          onRefresh={fetchQuotes}
        />
      </Box>
    </>
  );
}
